<template>
    <button
        ref="dropdownButtonElm"
        class="focus:outline-none focus:ring-non font-medium text-sm text-center inline-flex items-center rounded-[3px] bg-[#CB5FA0] hover:bg-[#b04e89] text-white px-2 py-1 cursor-pointer"
        type="button"
        @mouseover="onMouseOver"
        @mouseleave="onMouseLeave">
        <span
            class="inline-flex items-center font-bold text-lg leading-none tracking-wide"
            >{{ selectedFormatObj.title }}</span
        >
        <i-tabler-chevron-down
            v-if="!dropdownOpened"
            class="w-5 h-5 ms-2 icon-stroke-2"></i-tabler-chevron-down>
        <i-tabler-chevron-up v-else class="w-5 h-5 ms-2"></i-tabler-chevron-up>
    </button>

    <tooltip-bubble id="tooltip-select-model" ref="tooltipFormatElm">{{
        $t('format.main_tooltip')
    }}</tooltip-bubble>

    <!-- Dropdown menu -->
    <div
        ref="dropdownMenuElm"
        class="z-10 hidden bg-white divide-y divide-gray-100 rounded-lg shadow-xl max-w-[500px] dark:bg-gray-700 dark:divide-gray-600 overflow-y-auto">
        <div class="text-gray-500 px-4 py-2 text-base">
            {{ $t('format.dropdown_heading') }}
        </div>
        <ul class="space-y-1 text-gray-700 dark:text-gray-200">
            <li v-for="(item, key) in formats" :key="key">
                <div
                    class="flex p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-600">
                    <div class="ms-2 w-full me-2">
                        <label
                            class="font-bold text-gray-900 dark:text-gray-200 cursor-pointer"
                            @click="onSelect(key)">
                            <div class="flex items-center text-base">
                                {{ item.title }}
                            </div>
                            <p
                                class="font-normal text-base text-gray-500 dark:text-gray-300">
                                {{ $t(`format.${item.ext}_description`) }}
                            </p>
                            <p
                                v-if="item.ext === 'png'"
                                class="font-normal text-base text-gray-500 dark:text-gray-300 mt-2"
                                v-html="$t('format.png_warning')"></p>
                        </label>
                    </div>
                </div>
            </li>
        </ul>
    </div>
</template>

<script lang="ts" setup>
import {
    type DropdownOptions,
    type DropdownInterface,
    Tooltip,
    Dropdown,
    type InstanceOptions,
} from 'flowbite';
import TooltipBubble from '~/components/TooltipBubble.vue';
import { formats } from '~/helpers/data';
import { debounce } from '~/helpers/utils';

interface Props {
    format: string;
}

const props = withDefaults(defineProps<Props>(), {
    // model: 'geminiFlash',
});

const emit = defineEmits(['update']);

const selectedFormat = ref(props.format);
const tooltipFormatElm = ref<typeof TooltipBubble | null>(null);
let tooltip: Tooltip | null = null;
const dropdownOpened = ref(false);

const selectedFormatObj = computed(() => {
    const key = selectedFormat.value as keyof typeof formats;
    return formats[key];
});

const onSelect = (format: keyof typeof formats) => {
    selectedFormat.value = format;
    dropdown?.hide();
    emit('update', format);
};

// set the dropdown menu element
const dropdownButtonElm = ref<HTMLElement | null>(null);
// set the element that trigger the dropdown menu on click
const dropdownMenuElm = ref<HTMLElement | null>(null);

// dropdown options
const options: DropdownOptions = {
    placement: 'bottom',
    triggerType: 'click',
    offsetSkidding: 20,
    offsetDistance: 10,
    delay: 300,
    onShow: () => {
        tooltip?.hide();
        tooltip?.destroy();
        tooltip = null;
        dropdownOpened.value = true;
        // resize the dropdown menu to fit the window
        onWindowResize();
    },
    onHide: () => {
        dropdownOpened.value = false;
    },
};

// dropdown instance options object
const instanceOptions: InstanceOptions = {
    id: 'dropdownMenu',
    override: true,
};

let dropdown: DropdownInterface | null = null;

const onMouseOver = (e: MouseEvent) => {
    if (tooltip === null) {
        tooltip = new Tooltip(
            tooltipFormatElm?.value?.$el,
            e.target as HTMLElement,
            {
                placement: 'right',
                triggerType: 'none',
            }
        );
        tooltip.show();
    }
};

const onMouseLeave = () => {
    tooltip?.hide();
    tooltip?.destroy();
    tooltip = null;
};

// on window resize, update height of the dropdown menu to fit the window
const onWindowResize = debounce(() => {
    // if bottom edge of dropdownMenuElm.value is out of window,
    // update the height of the dropdown menu
    if (dropdownMenuElm.value) {
        const rect = dropdownMenuElm.value.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const bottomEdge = rect.bottom;

        if (bottomEdge > windowHeight) {
            dropdownMenuElm.value.style.maxHeight =
                windowHeight - rect.top - 20 + 'px';
        } else {
            dropdownMenuElm.value.style.maxHeight = '';
        }
    }
}, 100);

onMounted(() => {
    window.addEventListener('resize', onWindowResize, false);

    dropdown = new Dropdown(
        dropdownMenuElm.value,
        dropdownButtonElm.value,
        options,
        instanceOptions
    );
});

onUnmounted(() => {
    window.removeEventListener('resize', onWindowResize, false);
});
</script>
